import React from "react";
import styled, { css } from "styled-components";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faArrowLeft,
  faChevronDown,
  faCheck,
  faCommentDots,
} from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ContentTagType, Filter, FilterInfo, Unit } from "../common/api";
import {
  AnalyticsContext,
  FilterDeselectAllEvent,
  FilterDeselectEvent,
  FilterSelectEvent,
} from "../common/context/AnalyticsContext";
import { SelectedFiltersContext } from "../common/context/SelectedFiltersContext";
import { TabEnum } from "../common/context/TabsContext";
import { UnitSearch } from "./UnitSearch";
import {
  COLOR_BLACK,
  COLOR_SURFACE_CONTAINER,
  COLOR_SURFACE_CONTAINER_DARK,
  COLOR_TEXT_PRIMARY,
  COLOR_NEUTRAL_BORDER,
  COLOR_WHITE,
} from "../common/colors";
import { ELEVATION_1 } from "../common/elevation";
import { SPACE_SM, SPACE_LG } from "../common/spacing";
import { BORDER_WIDTH, GENERIC_ITEM_BORDER_RADIUS } from "../common/constants";
import { PrimaryButton, SECTION_SPACING } from "../common/CommonStyles";

const PILL_RADIUS = 30;
const PILL_PADDING_HORIZONTAL = 15;
const PILL_PADDING_VERTICAL = 10;
const PILL_FONT_SIZE = 13;
const PILL_LINE_HEIGHT = 17;
const SEARCH_BAR_WIDTH = 225;
const SEARCH_BAR_EXPANDED_WIDTH = 340;
const SEARCH_BUTTON_INSET = 4;
const MENU_PADDING = 6;
const MENU_ITEM_RADIUS = 12;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE_SM}px;
  padding: ${SECTION_SPACING}px 0;
`;

const pillSurface = css<{ $isSelected: boolean }>`
  border-radius: ${PILL_RADIUS}px;
  background: ${COLOR_WHITE};
  box-shadow: ${(p) =>
    p.$isSelected
      ? `0 0 0 2px ${COLOR_SURFACE_CONTAINER_DARK} inset, ${ELEVATION_1}`
      : ELEVATION_1};
  border: none;
  padding: ${PILL_PADDING_VERTICAL}px ${PILL_PADDING_HORIZONTAL}px;
  font-size: ${PILL_FONT_SIZE}px;
  font-weight: 400;
  color: ${COLOR_TEXT_PRIMARY};
`;

const FilterBubble = styled.span<{ $isSelected: boolean }>`
  ${pillSurface}
  cursor: pointer;
  line-height: ${PILL_LINE_HEIGHT}px;
`;

const SearchBar = styled.div<{ $isSelected: boolean; $isExpanded: boolean }>`
  ${pillSurface}
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$isExpanded ? "flex-start" : "center")};
  width: ${(p) =>
    p.$isExpanded ? SEARCH_BAR_EXPANDED_WIDTH : SEARCH_BAR_WIDTH}px;
  max-width: 100%;
  transition: width 120ms ease-out;
  cursor: text;

  & > svg {
    color: ${COLOR_TEXT_PRIMARY};
    margin-right: ${SPACE_SM}px;
    flex-shrink: 0;
    pointer-events: none;
  }
`;

const SearchButton = styled(PrimaryButton)<{ $enabled: boolean }>`
  flex-shrink: 0;
  margin: -${PILL_PADDING_VERTICAL -
    SEARCH_BUTTON_INSET}px -${PILL_PADDING_HORIZONTAL -
    SEARCH_BUTTON_INSET}px -${PILL_PADDING_VERTICAL - SEARCH_BUTTON_INSET}px
    auto;
  border-radius: ${PILL_RADIUS}px;
  padding: ${PILL_PADDING_VERTICAL - SEARCH_BUTTON_INSET}px ${SPACE_LG}px;
  font-size: ${PILL_FONT_SIZE}px;
  font-weight: 500;
  line-height: ${PILL_LINE_HEIGHT}px;
  opacity: ${(p) => (p.$enabled ? 1 : 0.2)};
  pointer-events: ${(p) => (p.$enabled ? "auto" : "none")};
`;

const PillButton = styled.button<{ $isSelected: boolean }>`
  ${pillSurface}
  display: inline-flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  line-height: ${PILL_LINE_HEIGHT}px;
  cursor: pointer;
`;

const SearchField = styled.input<{ $isExpanded: boolean }>`
  border: none;
  outline: none;
  background: transparent;
  height: ${PILL_LINE_HEIGHT}px;
  flex: ${(p) => (p.$isExpanded ? "1 1 auto" : "0 1 auto")};
  min-width: 0;
  font-size: ${PILL_FONT_SIZE}px;
  padding: 0;
  color: ${COLOR_TEXT_PRIMARY};

  ::placeholder {
    color: ${COLOR_TEXT_PRIMARY};
  }
`;

const AgeDropdownToggle = styled(Dropdown.Toggle)<{ $isSelected: boolean }>`
  ${pillSurface}
  display: inline-flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  line-height: ${PILL_LINE_HEIGHT}px;

  /* Replace Bootstrap's default caret with FontAwesome chevron. */
  &::after {
    display: none;
  }

  &:hover,
  &:focus,
  &:active {
    ${pillSurface}
  }
`;

const AgeDropdownMenu = styled(Dropdown.Menu)`
  border-radius: ${GENERIC_ITEM_BORDER_RADIUS}px;
  border: ${BORDER_WIDTH}px solid ${COLOR_NEUTRAL_BORDER};
  padding: ${MENU_PADDING}px;
`;

const AgeDropdownItem = styled(Dropdown.Item)`
  display: flex;
  align-items: center;
  gap: ${SPACE_SM}px;
  border-radius: ${MENU_ITEM_RADIUS}px;
  padding: ${SPACE_SM}px ${SPACE_LG}px;
  font-size: 16px;
  color: ${COLOR_BLACK};

  &&.active,
  &&:active {
    background: transparent;
    color: ${COLOR_BLACK};
  }

  &&:hover,
  &&:focus {
    background: ${COLOR_SURFACE_CONTAINER};
    color: ${COLOR_BLACK};
  }
`;

const CheckSlot = styled.span`
  display: inline-flex;
  justify-content: center;
  width: 16px;
  flex-shrink: 0;
`;

export type UnitFilters = {
  filterInfo?: FilterInfo;
  selectedFilters: ReadonlyMap<number, readonly string[]>;
  isFiltering: boolean;
  clearFilter: (filter: Filter) => void;
  updateFilter: (
    filter: Filter,
    newValue: string,
    isSingleItemFilter?: boolean
  ) => void;
  matches: (unit: Unit) => boolean;
  sectionTitle: (defaultTitle: string) => string;
};

export const useUnitFilters = (
  tab: TabEnum | null,
  filterInfo?: FilterInfo
): UnitFilters => {
  const [selectedFilters, setSelectedFilters] = React.useState<
    ReadonlyMap<number, readonly string[]>
  >(new Map());
  const { logEvent } = React.useContext(AnalyticsContext);
  const { selectedFiltersRef } = React.useContext(SelectedFiltersContext);
  const { t } = useTranslation();
  const history = useHistory();
  const isSeeded = React.useRef(false);

  React.useEffect(() => {
    if (!filterInfo || isSeeded.current) {
      return;
    }
    isSeeded.current = true;

    const saved =
      tab !== null && selectedFiltersRef.current?.tab === tab
        ? selectedFiltersRef.current.filters
        : null;
    const urlFilters = new URLSearchParams(
      tab === null ? "" : window.location.search
    );
    const seededFilters = new Map<number, readonly string[]>();
    filterInfo.filters.forEach((filter) => {
      const fromUrl = urlFilters.get(filter.key.toString());
      if (fromUrl) {
        seededFilters.set(filter.key, fromUrl.split(","));
      } else {
        seededFilters.set(filter.key, saved?.get(filter.key) ?? []);
      }
    });
    setSelectedFilters(seededFilters);
  }, [filterInfo, tab, selectedFiltersRef]);

  React.useEffect(() => {
    if (tab === null || selectedFilters.size === 0) {
      return;
    }
    selectedFiltersRef.current = { tab, filters: selectedFilters };

    const newUrl = new URL(window.location.href);
    selectedFilters.forEach((values, key) => {
      if (values.length > 0) {
        newUrl.searchParams.set(key.toString(), values.join(","));
      } else {
        newUrl.searchParams.delete(key.toString());
      }
    });
    history.replace(newUrl.pathname + newUrl.search, history.location.state);
  }, [selectedFilters, tab, selectedFiltersRef, history]);

  const selectedFiltersForLogging = (
    filters: ReadonlyMap<number, readonly string[]>
  ): Map<string, readonly string[]> => {
    const named = new Map<string, readonly string[]>();
    filters.forEach((value, key) => {
      const name = filterInfo?.filters.find((it) => it.key === key)?.name;
      if (name !== undefined) {
        named.set(name, value);
      }
    });
    return named;
  };

  const clearFilter = (filter: Filter) => {
    setSelectedFilters((previous) => {
      const updated = new Map(previous).set(filter.key, []);
      if (previous.get(filter.key)?.length) {
        logEvent(
          new FilterDeselectAllEvent(
            filter.name,
            selectedFiltersForLogging(updated)
          )
        );
      }
      return updated;
    });
  };

  const updateFilter = (
    filter: Filter,
    newValue: string,
    isSingleItemFilter = false
  ) => {
    setSelectedFilters((previous) => {
      let newFilterList: string[] = [...(previous.get(filter.key) ?? [])];
      const isAlreadySelected = newFilterList.includes(newValue);

      if (isSingleItemFilter) {
        newFilterList = isAlreadySelected ? [] : [newValue];
      } else if (isAlreadySelected) {
        newFilterList = newFilterList.filter((it) => it !== newValue);
      } else {
        newFilterList = newFilterList.concat(newValue);
      }

      const updated = new Map(previous).set(filter.key, newFilterList.sort());
      logEvent(
        isAlreadySelected
          ? new FilterDeselectEvent(
              filter.name,
              newValue,
              selectedFiltersForLogging(updated)
            )
          : new FilterSelectEvent(
              filter.name,
              newValue,
              selectedFiltersForLogging(updated)
            )
      );
      return updated;
    });
  };

  const ageTitle = (ageFilters: readonly string[]) => {
    if (ageFilters.length === 0) {
      return "";
    }
    if (ageFilters.length === 1) {
      return t("single_age_filter_title", { age: ageFilters[0] });
    }
    const allAges =
      filterInfo?.filters
        .find((filter) => filter.key === ContentTagType.Age)
        ?.values.slice()
        .sort() ?? [];
    const firstSelectedIndex = allAges.indexOf(ageFilters[0]);
    const hasNoGaps =
      firstSelectedIndex >= 0 &&
      ageFilters.every(
        (age, index) => age === allAges[index + firstSelectedIndex]
      );
    return hasNoGaps
      ? t("sequential_age_filters_title", {
          from: ageFilters[0],
          to: ageFilters[ageFilters.length - 1],
        })
      : t("gapped_age_filter_title", { range: ageFilters.join(", ") });
  };

  const sectionTitle = (defaultTitle: string) => {
    const ageFilters = selectedFilters.get(ContentTagType.Age) ?? [];
    const subjectFilter = selectedFilters.get(ContentTagType.Subject)?.at(0);

    let subjectTitle = defaultTitle;
    if (subjectFilter) {
      subjectTitle =
        filterInfo?.section_title_overrides?.[subjectFilter] ??
        t("filter_generic_subject", { subject: subjectFilter });
    } else if (ageFilters.length > 0) {
      subjectTitle = t("filter_no_subject");
    }

    const agePortion = ageTitle(ageFilters);
    return agePortion
      ? t("main_units_title_with_age", {
          subjectFilter: subjectTitle,
          ageFilter: agePortion,
        })
      : subjectTitle;
  };

  const matches = (unit: Unit) =>
    Array.from(selectedFilters.entries()).every(
      ([key, values]) =>
        values.length === 0 ||
        !!unit.content_tags?.[key]?.some((it) => values.includes(it))
    );

  return {
    filterInfo,
    selectedFilters,
    isFiltering: Array.from(selectedFilters.values()).some(
      (values) => values.length > 0
    ),
    clearFilter,
    updateFilter,
    matches,
    sectionTitle,
  };
};

export const UnitFilterControls: React.FC<{
  filters: UnitFilters;
  search?: UnitSearch;
}> = ({ filters, search }) => {
  const { t } = useTranslation();
  const [searchFocused, setSearchFocused] = React.useState(false);
  const searchFieldRef = React.useRef<HTMLInputElement>(null);

  const { filterInfo, selectedFilters, clearFilter, updateFilter } = filters;
  const ageFilter = filterInfo?.filters.find(
    (filter) => filter.key === ContentTagType.Age
  );
  const subjectFilters = (filterInfo?.filters ?? [])
    .filter((filter) => filter.key !== ContentTagType.Age)
    .sort((a, b) => a.key - b.key);
  const selectedAge = ageFilter
    ? selectedFilters.get(ageFilter.key)?.at(0)
    : undefined;
  const searchPlaceholder = searchFocused
    ? t("search_placeholder_focused")
    : t("search_placeholder");

  const isSearchButtonEnabled = (search?.query ?? "").trim().length > 0;
  const isBarExpanded = searchFocused || isSearchButtonEnabled;

  const searchBar = search && (
    <SearchBar
      $isSelected={searchFocused}
      $isExpanded={isBarExpanded}
      onMouseDown={(e) => {
        if (e.target !== searchFieldRef.current) {
          e.preventDefault();
        }
      }}
      onClick={() => searchFieldRef.current?.focus()}
    >
      <FontAwesomeIcon icon={faMagnifyingGlass} />
      <SearchField
        ref={searchFieldRef}
        type="text"
        value={search.query}
        $isExpanded={isBarExpanded}
        size={Math.max(search.query.length, searchPlaceholder.length)}
        placeholder={searchPlaceholder}
        onChange={(e) => search.setQuery(e.target.value)}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            search.runSearch();
          } else if (e.key === "Escape") {
            searchFieldRef.current?.blur();
          }
        }}
      />
      {isBarExpanded && (
        <SearchButton
          $enabled={isSearchButtonEnabled}
          onClick={search.runSearch}
        >
          {t("search_button")}
        </SearchButton>
      )}
    </SearchBar>
  );

  if (search && search.committedQuery !== null) {
    return (
      <ControlsRow>
        <PillButton $isSelected={false} onClick={search.clearSearch}>
          <FontAwesomeIcon icon={faArrowLeft} />
          {t("search_back")}
        </PillButton>
        {searchBar}
        {search.canGiveFeedback && (
          <PillButton
            $isSelected={search.isFeedbackFormOpen}
            onClick={search.toggleFeedbackForm}
          >
            <FontAwesomeIcon icon={faCommentDots} />
            {t("search_feedback_button")}
          </PillButton>
        )}
      </ControlsRow>
    );
  }

  return (
    <ControlsRow>
      {searchBar}
      {ageFilter && (
        <Dropdown>
          <AgeDropdownToggle
            variant="light"
            id="age-filter-dropdown"
            $isSelected={!!selectedAge}
          >
            {selectedAge ? ageFilter.name + ": " + selectedAge : ageFilter.name}
            <FontAwesomeIcon icon={faChevronDown} />
          </AgeDropdownToggle>
          <AgeDropdownMenu>
            <AgeDropdownItem
              active={!selectedAge}
              onClick={() => clearFilter(ageFilter)}
            >
              <CheckSlot>
                {!selectedAge && <FontAwesomeIcon icon={faCheck} />}
              </CheckSlot>
              {t("filter_all_ages")}
            </AgeDropdownItem>
            {ageFilter.values.map((value) => (
              <AgeDropdownItem
                key={value}
                active={selectedAge === value}
                onClick={() => updateFilter(ageFilter, value, true)}
              >
                <CheckSlot>
                  {selectedAge === value && <FontAwesomeIcon icon={faCheck} />}
                </CheckSlot>
                {ageFilter.name + ": " + value}
              </AgeDropdownItem>
            ))}
          </AgeDropdownMenu>
        </Dropdown>
      )}
      {subjectFilters.map((filter) =>
        filter.values.map((value) => (
          <FilterBubble
            key={`${filter.key}-${value}`}
            onClick={() => {
              updateFilter(
                filter,
                value,
                filter.key === ContentTagType.Subject
              );
            }}
            $isSelected={!!selectedFilters.get(filter.key)?.includes(value)}
          >
            {value}
          </FilterBubble>
        ))
      )}
    </ControlsRow>
  );
};
